import React, { useState, useEffect } from "react";
import Constants from "../components/Constant.js";
import {
  TextField,
  Button,
  Container,
  Box,
  Typography,
  Grid,
  Autocomplete,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CardMedia,
} from "@mui/material";
import MainLayout from "../components/MainLayout.js";
import backgroundImage from "../assets/backgroundHome.png";
import gasolinaComum from "../assets/iconeGasolinaComum.png";
import gasolinaAditivada from "../assets/iconeGasolinaAditivada.png";
import etanol from "../assets/iconeEtanol.png";
import diesel500 from "../assets/iconeDieselS500.png";
import dieselS10 from "../assets/iconeDieselS10.png";
import ARLA32 from "../assets/iconeARLA32.png";

export default function PrecoCombustivel() {
  const [postos, setPostos] = useState([]);
  const [prefeituras, setPrefeituras] = useState([]);
  const [filteredPostos, setFilteredPostos] = useState([]);
  const [selectedPosto, setSelectedPosto] = useState(null);
  const [selectedPrefeitura, setSelectedPrefeitura] = useState(null);
  const [searchPosto, setSearchPosto] = useState("");
  const [precos, setPrecos] = useState({
    gasolinaComum: "",
    gasolinaAditivada: "",
    etanol: "",
    diesel500: "",
    dieselS10: "",
    ARLA32: "",
  });
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const combustiveis = [
    { id: "gasolinaComum", nome: "Gasolina Comum", imagem: gasolinaComum },
    { id: "gasolinaAditivada", nome: "Gasolina Aditivada", imagem: gasolinaAditivada },
    { id: "etanol", nome: "Etanol", imagem: etanol },
    { id: "diesel500", nome: "Diesel S500", imagem: diesel500 },
    { id: "dieselS10", nome: "Diesel S10", imagem: dieselS10 },
    { id: "ARLA32", nome: "ARLA32", imagem: ARLA32 },
  ];

  // Buscar lista de postos e prefeituras
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Buscar prefeituras
        const prefeiturasResponse = await fetch(Constants.API_CONSULTAR_PREFEITURA, {
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });
        const prefeiturasData = await prefeiturasResponse.json();

        if (prefeiturasResponse.ok) {
          setPrefeituras(prefeiturasData.prefeituras || []);
        } else {
          showSnackbar("Erro ao buscar prefeituras.", "error");
        }
      } catch (error) {
        console.error("Erro ao conectar com o servidor:", error);
        showSnackbar("Erro ao conectar com o servidor.", "error");
      }
    };
    fetchData();
  }, [token]);

  // Buscar postos quando uma prefeitura for selecionada
  useEffect(() => {
    const fetchPostos = async () => {
      if (!selectedPrefeitura?.PRE_ID) {
        setPostos([]);
        setFilteredPostos([]);
        return;
      }

      try {
        const postosResponse = await fetch(`${Constants.API_CONSULTAR_POSTO_PREFEITURA}/${selectedPrefeitura.PRE_ID}`, {
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });
        const postosData = await postosResponse.json();

        if (postosResponse.ok) {
          setPostos(postosData.postos || []);
          setFilteredPostos(postosData.postos || []);
        } else {
          showSnackbar("Erro ao buscar postos.", "error");
        }
      } catch (error) {
        console.error("Erro ao conectar com o servidor:", error);
        showSnackbar("Erro ao conectar com o servidor.", "error");
      }
    };
    fetchPostos();
  }, [selectedPrefeitura, token]);

  // Filtrar postos baseado na busca
  useEffect(() => {
    if (searchPosto) {
      const filtered = postos.filter((posto) =>
        posto.POS_RAZAO_SOCIAL && posto.POS_RAZAO_SOCIAL.toLowerCase().includes(searchPosto.toLowerCase())
      );
      setFilteredPostos(filtered);
    } else {
      setFilteredPostos(postos);
    }
  }, [searchPosto, postos]);

  const handlePostoChange = (_, newValue) => {
    setSelectedPosto(newValue);
    setErrors({});
    // Limpar os preços quando selecionar um novo posto
    setPrecos({
      gasolinaComum: "",
      gasolinaAditivada: "",
      etanol: "",
      diesel500: "",
      dieselS10: "",
      ARLA32: "",
    });
  };

  const handlePrefeituraChange = (_, newValue) => {
    setSelectedPrefeitura(newValue);
    setSelectedPosto(null); // Limpa o posto selecionado quando mudar a prefeitura
    setPrecos({
      gasolinaComum: "",
      gasolinaAditivada: "",
      etanol: "",
      diesel500: "",
      dieselS10: "",
      ARLA32: "",
    });
  };

  const handlePrecoChange = (combustivelId, value) => {
    // Se o valor estiver vazio, mantém vazio
    if (value === "") {
      setPrecos(prev => ({
        ...prev,
        [combustivelId]: ""
      }));
      return;
    }

    // Remove tudo que não é número
    let newValue = value.replace(/\D/g, "");
    
    // Se tiver apenas um número
    if (newValue.length === 1) {
      newValue = `0,00${newValue}`;
    }
    // Se tiver dois números
    else if (newValue.length === 2) {
      newValue = `0,0${newValue}`;
    }
    // Se tiver três números
    else if (newValue.length === 3) {
      newValue = `0,${newValue}`;
    }
    // Se tiver mais de três números
    else if (newValue.length > 3) {
      // Remove zeros à esquerda desnecessários
      newValue = newValue.replace(/^0+/, "");
      if (newValue === "") newValue = "0";
      
      // Separa os últimos 3 como decimais
      const integerPart = newValue.slice(0, -3).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      const decimalPart = newValue.slice(-3);
      newValue = `${integerPart},${decimalPart}`;
    }

    setPrecos(prev => ({
      ...prev,
      [combustivelId]: newValue
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    Object.entries(precos).forEach(([key, value]) => {
      if (value.trim()) {
        const numericValue = Number(value.replace(",", "."));
        if (isNaN(numericValue) || numericValue <= 0) {
          newErrors[key] = "Preço deve ser maior que zero";
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!selectedPosto || !selectedPrefeitura) {
      showSnackbar("Selecione um posto e uma prefeitura para editar.", "error");
      return;
    }

    if (!validateForm()) {
      showSnackbar("Por favor, corrija os erros no formulário.", "error");
      return;
    }

    // Mapeamento dos IDs dos combustíveis
    const combustivelIds = {
      gasolinaComum: 2,
      gasolinaAditivada: 3,
      etanol: 1,
      diesel500: 5,
      dieselS10: 4,
      ARLA32: 6
    };

    // Cria o array de combustíveis apenas com os preços preenchidos
    const combustiveis = Object.entries(precos)
      .filter(([_, valor]) => valor.trim() !== "") // Filtra apenas os preços preenchidos
      .map(([tipo, valor]) => ({
        COM_ID: combustivelIds[tipo],
        PRE_ID: selectedPrefeitura.PRE_ID,
        POS_ID: selectedPosto.POS_ID,
        SALDO: parseFloat(valor.replace(/\./g, "").replace(",", "."))
      }));

    try {
      const response = await fetch(`${Constants.API_CADASTRAR_PRECO_COMBUSTIVEL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          PRE_ID: selectedPrefeitura.PRE_ID,
          POS_ID: selectedPosto.POS_ID,
          COMBUSTIVEIS: combustiveis
        }),
      });

      if (response.ok) {
        showSnackbar("Preços cadastrados com sucesso!", "success");
        // Limpar os preços após o cadastro bem-sucedido
        setPrecos({
          gasolinaComum: "",
          gasolinaAditivada: "",
          etanol: "",
          diesel500: "",
          dieselS10: "",
          ARLA32: "",
        });
      } else {
        const data = await response.json();
        showSnackbar(data.message || "Erro ao cadastrar preços.", "error");
      }
    } catch (error) {
      console.error("Erro ao conectar com o servidor:", error);
      showSnackbar("Erro ao conectar com o servidor.", "error");
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <MainLayout>
      <Box
        sx={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backgroundBlendMode: "overlay",
          padding: { xs: 1, sm: 2 },
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: "background.paper", borderRadius: 2, boxShadow: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}>
              Preços dos Combustíveis
            </Typography>

            {/* Seleção da Prefeitura e Posto */}
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={prefeituras}
                  getOptionLabel={(option) => option.PRE_NOME || ""}
                  isOptionEqualToValue={(option, value) => option?.PRE_ID === value?.PRE_ID}
                  value={selectedPrefeitura}
                  onChange={handlePrefeituraChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Selecione uma Prefeitura"
                      fullWidth
                      margin="normal"
                      size="small"
                      sx={{ "& .MuiInputBase-root": { height: "48px" } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={filteredPostos}
                  getOptionLabel={(option) => option.POS_RAZAO_SOCIAL || ""}
                  isOptionEqualToValue={(option, value) => option?.POS_ID === value?.POS_ID}
                  value={selectedPosto}
                  onChange={handlePostoChange}
                  onInputChange={(_, newInputValue) => {
                    setSearchPosto(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Selecione um Posto"
                      fullWidth
                      margin="normal"
                      size="small"
                      sx={{ "& .MuiInputBase-root": { height: "48px" } }}
                    />
                  )}
                />
              </Grid>

              {selectedPosto && selectedPrefeitura && (
                <>
                  {/* Cards dos Combustíveis */}
                  <Grid container item spacing={1}>
                    {combustiveis.map((combustivel) => (
                      <Grid item xs={6} sm={4} md={4} key={combustivel.id}>
                        <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                          <CardMedia
                            component="img"
                            height="60"
                            image={combustivel.imagem}
                            alt={combustivel.nome}
                            sx={{ 
                              objectFit: "contain", 
                              p: 1,
                              height: { xs: "50px", sm: "60px" }
                            }}
                          />
                          <CardContent sx={{ flexGrow: 1, p: 1 }}>
                            <Typography 
                              gutterBottom 
                              variant="subtitle1" 
                              component="div" 
                              align="center"
                              sx={{ 
                                fontSize: { xs: "0.8rem", sm: "0.9rem" },
                                mb: 1
                              }}
                            >
                              {combustivel.nome}
                            </Typography>
                            <TextField
                              fullWidth
                              label="Preço"
                              value={precos[combustivel.id]}
                              onChange={(e) => handlePrecoChange(combustivel.id, e.target.value)}
                              error={!!errors[combustivel.id]}
                              helperText={errors[combustivel.id] || ""}
                              size="small"
                              sx={{ 
                                "& .MuiInputBase-root": { 
                                  height: { xs: "40px", sm: "48px" } 
                                },
                                mt: 0
                              }}
                              inputProps={{ maxLength: 7 }}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Botão de Salvar */}
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        sx={{ 
                          height: { xs: "40px", sm: "48px" }, 
                          width: { xs: "100%", sm: "200px" }
                        }}
                      >
                        Cadastrar Preços
                      </Button>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </Container>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  );
}